import React, { useState, useCallback } from "react";
import { compareRuns, compareLatestVsPrevious } from "../../../lib/researchApi";
import GovernanceBadge from "../shared/GovernanceBadge";
import TrendIndicator from "../shared/TrendIndicator";

const STRATEGY_OPTIONS = ["wheel", "earnings", "tactical_spread", "regime_spread"];
const STRATEGY_LABELS = {
    wheel:           "Wheel",
    earnings:        "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
};

const METRIC_LABELS = {
    overfit_score:              "Overfit Score",
    risk_of_ruin:               "Risk of Ruin",
    walk_forward_degradation:   "WF Degradation",
    win_rate:                   "Win Rate",
    max_drawdown:               "Max Drawdown",
    confidence_interval_lower:  "CI Lower",
    confidence_interval_upper:  "CI Upper",
    expectancy:                 "Expectancy",
};

function DeltaCell({ delta, direction, higherIsBetter }) {
    if (delta == null) return <span style={{ color: "var(--savvy-dim)" }}>—</span>;
    const improved = higherIsBetter ? delta > 0 : delta < 0;
    const color = direction === "improved" ? "#4ade80" : direction === "worsened" ? "#f87171" : "var(--savvy-muted)";
    return (
        <span style={{ color, fontWeight: 600, fontSize: "12px" }}>
            {delta > 0 ? "+" : ""}{delta.toFixed(4)}
            {direction === "improved" && " ↑"}
            {direction === "worsened" && " ↓"}
        </span>
    );
}

export default function ComparisonSection() {
    const [mode, setMode] = useState("latest_vs_previous");
    const [runA, setRunA] = useState("");
    const [runB, setRunB] = useState("");
    const [strategy, setStrategy] = useState("wheel");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const runComparison = useCallback(async () => {
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            let data;
            if (mode === "latest_vs_previous") {
                data = await compareLatestVsPrevious(strategy);
            } else {
                const a = parseInt(runA, 10);
                const b = parseInt(runB, 10);
                if (!a || !b) { setError("Enter valid run IDs"); setLoading(false); return; }
                data = await compareRuns(a, b);
            }
            setResult(data);
        } catch (e) {
            setError(e?.response?.data?.error || "Comparison failed");
        } finally {
            setLoading(false);
        }
    }, [mode, strategy, runA, runB]);

    const metrics = result?.metrics || {};
    const regressionWarnings = result?.regression_warnings || [];
    const ctx = result?.context || {};

    return (
        <div>
            {/* Controls */}
            <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Mode
                    </div>
                    <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: "1px solid var(--savvy-border)" }}>
                        {[
                            { id: "latest_vs_previous", label: "Latest vs Previous" },
                            { id: "run_ids", label: "By Run IDs" },
                        ].map(m => (
                            <button
                                key={m.id}
                                onClick={() => { setMode(m.id); setResult(null); setError(null); }}
                                style={{
                                    padding: "5px 12px",
                                    fontSize: "12px",
                                    background: mode === m.id ? "rgba(159,234,232,0.12)" : "transparent",
                                    border: "none",
                                    color: mode === m.id ? "var(--savvy-accent)" : "var(--savvy-muted)",
                                    cursor: "pointer",
                                    fontWeight: mode === m.id ? 600 : 400,
                                }}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                </div>

                {mode === "latest_vs_previous" ? (
                    <div>
                        <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Strategy</div>
                        <select
                            value={strategy}
                            onChange={e => { setStrategy(e.target.value); setResult(null); }}
                            style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 6, color: "var(--savvy-text)", fontSize: "12px", padding: "5px 10px" }}
                        >
                            {STRATEGY_OPTIONS.map(s => <option key={s} value={s}>{STRATEGY_LABELS[s]}</option>)}
                        </select>
                    </div>
                ) : (
                    <>
                        <div>
                            <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Baseline Run ID</div>
                            <input
                                type="number"
                                placeholder="e.g. 42"
                                value={runA}
                                onChange={e => setRunA(e.target.value)}
                                style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 6, color: "var(--savvy-text)", fontSize: "12px", padding: "5px 10px", width: 100 }}
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Current Run ID</div>
                            <input
                                type="number"
                                placeholder="e.g. 47"
                                value={runB}
                                onChange={e => setRunB(e.target.value)}
                                style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 6, color: "var(--savvy-text)", fontSize: "12px", padding: "5px 10px", width: 100 }}
                            />
                        </div>
                    </>
                )}

                <button
                    onClick={runComparison}
                    disabled={loading}
                    style={{
                        padding: "6px 16px",
                        borderRadius: 6,
                        fontSize: "12px",
                        fontWeight: 600,
                        background: "rgba(159,234,232,0.12)",
                        border: "1px solid rgba(159,234,232,0.3)",
                        color: "var(--savvy-accent)",
                        cursor: loading ? "wait" : "pointer",
                    }}
                >
                    {loading ? "Comparing…" : "Compare"}
                </button>
            </div>

            {error && <div style={{ color: "#f87171", fontSize: "13px", marginBottom: 12 }}>Error: {error}</div>}

            {result && (
                <>
                    {/* Context */}
                    <div style={{ display: "flex", gap: 16, marginBottom: 16, fontSize: "12px", color: "var(--savvy-muted)", flexWrap: "wrap" }}>
                        {ctx.strategy && <span>Strategy: <strong style={{ color: "var(--savvy-text)" }}>{STRATEGY_LABELS[ctx.strategy] || ctx.strategy}</strong></span>}
                        {ctx.baseline_run_id && <span>Baseline: <strong style={{ color: "var(--savvy-text)" }}>#{ctx.baseline_run_id}</strong></span>}
                        {ctx.current_run_id && <span>Current: <strong style={{ color: "var(--savvy-text)" }}>#{ctx.current_run_id}</strong></span>}
                        {result.run_a?.governance_state && (
                            <span>A: <GovernanceBadge state={result.run_a.governance_state} /></span>
                        )}
                        {result.run_b?.governance_state && (
                            <span>B: <GovernanceBadge state={result.run_b.governance_state} /></span>
                        )}
                    </div>

                    {/* Regression warnings */}
                    {regressionWarnings.length > 0 && (
                        <div style={{
                            padding: "10px 14px",
                            background: "rgba(220,38,38,0.08)",
                            border: "1px solid rgba(220,38,38,0.3)",
                            borderRadius: 8,
                            marginBottom: 16,
                        }}>
                            <div style={{ fontSize: "10px", fontWeight: 700, color: "#f87171", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                                Regression Warnings
                            </div>
                            {regressionWarnings.map((w, i) => (
                                <div key={i} style={{ fontSize: "12px", color: "rgba(255,255,255,0.75)", marginBottom: 3 }}>
                                    ↓ <strong>{METRIC_LABELS[w.metric] || w.metric}</strong>: {w.message}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Metric comparison table */}
                    {Object.keys(metrics).length > 0 ? (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                                        {["Metric", "Scope", "Baseline", "Current", "Delta", "Direction"].map(h => (
                                            <th key={h} style={{ textAlign: "left", fontSize: "10px", fontWeight: 600, color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.05em", padding: "8px 10px" }}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(metrics).map(([key, m]) => (
                                        <tr key={key} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                                            <td style={{ padding: "9px 10px", fontSize: "12px", color: "var(--savvy-text)", fontWeight: 500 }}>
                                                {METRIC_LABELS[m.field || key] || key}
                                            </td>
                                            <td style={{ padding: "9px 10px", fontSize: "11px", color: "var(--savvy-dim)" }}>
                                                {m.metric_scope?.replace(/_/g, " ")}
                                            </td>
                                            <td style={{ padding: "9px 10px", fontSize: "12px", color: "var(--savvy-muted)", fontVariantNumeric: "tabular-nums" }}>
                                                {m.value_a != null ? m.value_a.toFixed(4) : "—"}
                                            </td>
                                            <td style={{ padding: "9px 10px", fontSize: "12px", color: "var(--savvy-text)", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                                                {m.value_b != null ? m.value_b.toFixed(4) : "—"}
                                            </td>
                                            <td style={{ padding: "9px 10px" }}>
                                                <DeltaCell delta={m.delta} direction={m.direction} higherIsBetter={m.higher_is_better} />
                                            </td>
                                            <td style={{ padding: "9px 10px" }}>
                                                <TrendIndicator
                                                    direction={
                                                        m.direction === "improved" ? "improving" :
                                                        m.direction === "worsened" ? "worsening" : "stable"
                                                    }
                                                    showLabel
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{ color: "var(--savvy-dim)", fontSize: "12px", padding: "24px 0", textAlign: "center" }}>
                            No metric deltas available
                        </div>
                    )}
                </>
            )}

            {!result && !loading && !error && (
                <div style={{
                    padding: "40px 16px",
                    textAlign: "center",
                    color: "var(--savvy-dim)",
                    fontSize: "13px",
                    background: "var(--savvy-panel)",
                    borderRadius: 8,
                    border: "1px solid var(--savvy-border)",
                }}>
                    Select a comparison mode and click Compare
                </div>
            )}
        </div>
    );
}
